import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, LogIn, Search } from 'lucide-react';
import type {
  EnterpriseListItem,
  EnterpriseListResponse,
} from '@proptr/shared';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { Input } from '../components/ui/input.js';
import { cn, initials } from '../lib/utils.js';
import { client } from '../main.js';

const PAGE_SIZE_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

export function AgenciesPage() {
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);
  const [search, setSearch] = React.useState<string>('');
  // Debounced query so typing doesn't fire one request per keystroke.
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(t);
  }, [search]);

  const [data, setData] = React.useState<EnterpriseListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    setError(null);
    client.admin
      .listEnterprises({
        search: debouncedSearch || undefined,
        page,
        limit: pageSize,
      })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [debouncedSearch, page, pageSize]);

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Agencies
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            <span className="text-foreground">{total}</span> agencies on the platform
          </p>
        </div>

        <Card className="overflow-hidden p-0">
          <div className="flex items-center justify-between gap-4 border-b border-[hsl(var(--hairline))] px-5 py-3">
            <div className="relative w-full max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or company…"
                className="pl-10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[hsl(var(--hairline))]">
                  {[
                    'Agency',
                    'Market',
                    'Currency',
                    'Managers',
                    'Users',
                    'Proptrees',
                    'Listings',
                    '',
                  ].map((label, i) => (
                    <th
                      key={i}
                      className={cn(
                        'whitespace-nowrap px-5 py-3 text-left align-middle font-normal first:pl-5 last:pr-5',
                        i === 7 && 'text-right',
                      )}
                    >
                      <HeaderLabel>{label}</HeaderLabel>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-12 text-center font-mono text-[11px] text-destructive"
                    >
                      {error}
                    </td>
                  </tr>
                ) : items === null ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground"
                    >
                      Loading…
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-5 py-12 text-center font-mono text-[11px] text-muted-foreground"
                    >
                      No agencies found.
                    </td>
                  </tr>
                ) : (
                  items.map((ent) => <AgencyRow key={ent._id} agency={ent} />)
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[hsl(var(--hairline))] px-5 py-3.5">
            <div className="mono-label text-muted-foreground">
              Showing{' '}
              <span className="text-foreground">
                {start}–{end}
              </span>{' '}
              of <span className="text-foreground">{total}</span> results
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="mono-label text-muted-foreground">Page limit</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" variant="outline">
                      {pageSize}
                      <ChevronDown />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[5rem]">
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => {
                          setPageSize(Number(opt.value));
                          setPage(1);
                        }}
                        className={cn(
                          String(pageSize) === opt.value && 'text-foreground',
                        )}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function AgencyRow({ agency }: { agency: EnterpriseListItem }) {
  const navigate = useNavigate();
  const displayName =
    agency.companyName || agency.name || '—';
  const subtitle = agency.companyName && agency.name ? agency.name : null;

  const open = (e?: React.MouseEvent) => {
    const path = `/agency/${agency._id}`;
    if (e && (e.metaKey || e.ctrlKey || e.button === 1)) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(path);
  };

  return (
    <tr
      onClick={open}
      onAuxClick={(e) => {
        if (e.button === 1) open(e);
      }}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') open();
      }}
      className="cursor-pointer border-b border-[hsl(var(--hairline))] last:border-b-0 transition-colors hover:bg-muted/40 focus:bg-muted/40 focus:outline-none"
    >
      <Cell>
        <div className="flex items-center gap-3">
          <AgencyAvatar logo={agency.logo} name={displayName} />
          <div>
            <div className="whitespace-nowrap font-sans text-[14px] font-medium text-foreground">
              {displayName}
            </div>
            {subtitle && (
              <div className="whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                {subtitle}
              </div>
            )}
          </div>
        </div>
      </Cell>
      <Cell>
        <Plain value={agency.market ?? '—'} dim={!agency.market} />
      </Cell>
      <Cell>
        <Mono value={agency.currency ?? '—'} dim={!agency.currency} />
      </Cell>
      <Cell>
        <Mono
          value={agency.managersCount.toLocaleString()}
          dim={agency.managersCount === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={agency.usersCount.toLocaleString()}
          dim={agency.usersCount === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={agency.proptreesCount.toLocaleString()}
          dim={agency.proptreesCount === 0}
        />
      </Cell>
      <Cell>
        <Mono
          value={agency.listingsCount.toLocaleString()}
          dim={agency.listingsCount === 0}
        />
      </Cell>
      <Cell>
        <div
          className="flex justify-end"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
              >
                <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[12rem]">
              <DropdownMenuItem disabled>
                <span className="flex-1">Login as manager</span>
                <LogIn />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Cell>
    </tr>
  );
}

function Cell({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-5 py-4 align-middle first:pl-5 last:pr-5">{children}</td>
  );
}

function HeaderLabel({ children }: { children: React.ReactNode }) {
  return <div className="mono-label text-muted-foreground">{children}</div>;
}

function Plain({ value, dim }: { value: string; dim?: boolean }) {
  return (
    <div
      className={cn(
        'whitespace-nowrap font-sans text-[13px] font-medium text-foreground',
        dim && 'text-muted-foreground',
      )}
    >
      {value}
    </div>
  );
}

function Mono({ value, dim }: { value: string; dim?: boolean }) {
  return (
    <div
      className={cn(
        'whitespace-nowrap font-mono text-[12px] font-medium tabular-nums text-foreground/80',
        dim && 'text-muted-foreground',
      )}
    >
      {value}
    </div>
  );
}

function AgencyAvatar({ logo, name }: { logo: string | null; name: string }) {
  if (logo) {
    return (
      <img
        src={logo}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-md bg-muted object-contain"
      />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted font-mono text-[11px] font-bold uppercase">
      {initials(name)}
    </div>
  );
}
